import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import {
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Database,
  FileBarChart,
  History,
  Loader2,
  Send,
  Sparkles,
  TableProperties,
  Zap,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { PortalMarkdown } from "@/components/portal/shared/PortalMarkdown";
import { QuickQuestionCharts } from "@/components/portal/charts/QuickQuestionCharts";
import { QueryFeedback } from "@/components/portal/shared/feedback";
import { QueryHistorySidebar, type QueryHistoryItem } from "@/components/portal/shared/query-history";
import { useAuth } from "@/contexts/AuthContext";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { useIsMobile } from "@/hooks/use-mobile";
import { usePermissions } from "@/hooks/usePermissions";
import { canAuthorInsightReports } from "@/lib/insightReportsAccess";
import { parseUtcDate } from "@/lib/dateFormat";
import { PortalApiError } from "@/lib/portalApi";
import { sqlRagApi, type DataSource } from "@/services/api/sqlRag";
import type { ChartSpec } from "@/services/api/insightReports";
import {
  DEFAULT_QUICK_QUESTION_EFFORT,
  phaseLabel,
  quickQuestionApi,
  QUICK_QUESTION_EFFORTS,
  type QuickQuestionEffort,
  type QuickQuestionHistoryDetail,
  type QuickQuestionHistoryItem,
  type QuickQuestionResult,
} from "@/services/api/quickQuestion";
import { AddToInsightReportDialog } from "./AddToInsightReportDialog";
import { PromoteToReportingDialog } from "./PromoteToReportingDialog";
import { ResultsTable } from "./ResultsTable";
import {
  openReportWizardWithHandoff,
  useKbPromotion,
  type KbPromoteConfirmArgs,
  type KbPromoteError,
} from "./useKbPromotion";
import { useQuickQuestionJob, type QuickQuestionTurn } from "./useQuickQuestionJob";

// Knowledgebase DB "Quick Question" (plan kbdb-quick-question D15): ONE AI
// analyst with tools works through the data step by step — clearly distinct from Ask
// Database's pipelined, clarification-card flow next door. Transcript column,
// composer at the bottom, live phase text while running, own history.

export const Route = createFileRoute("/portal/knowledgebase-sql/quick-question")({
  component: QuickQuestionPage,
});

export const LAST_SOURCE_STORAGE_KEY = "lara:kbdb:qq:last-source";

function readLastSource(): string {
  try {
    return localStorage.getItem(LAST_SOURCE_STORAGE_KEY) ?? "";
  } catch {
    return "";
  }
}

function rememberSource(id: string) {
  try {
    if (id) localStorage.setItem(LAST_SOURCE_STORAGE_KEY, id);
  } catch {
    /* storage unavailable — the picker still works for this session */
  }
}

export const EFFORT_STORAGE_KEY = "lara:kbdb:qq:effort";

const EFFORT_LABELS: Record<QuickQuestionEffort, string> = {
  low: "Low",
  medium: "Medium",
  high: "High",
};

function isEffort(value: unknown): value is QuickQuestionEffort {
  return QUICK_QUESTION_EFFORTS.includes(value as QuickQuestionEffort);
}

function readEffort(): QuickQuestionEffort {
  try {
    const stored = localStorage.getItem(EFFORT_STORAGE_KEY);
    return isEffort(stored) ? stored : DEFAULT_QUICK_QUESTION_EFFORT;
  } catch {
    return DEFAULT_QUICK_QUESTION_EFFORT;
  }
}

function rememberEffort(effort: QuickQuestionEffort) {
  try {
    localStorage.setItem(EFFORT_STORAGE_KEY, effort);
  } catch {
    /* storage unavailable — the choice still applies for this session */
  }
}

function SqlBlock({ sql }: { sql: string }) {
  const [open, setOpen] = useState(false);
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <Button variant="ghost" size="sm" className="h-7 px-2 text-xs">
          {open ? <ChevronDown className="mr-1 h-3 w-3" /> : <ChevronRight className="mr-1 h-3 w-3" />}
          SQL run
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <pre className="mt-1 max-h-64 overflow-auto rounded-md bg-slate-950 p-3 text-xs text-slate-100">
          <code>{sql}</code>
        </pre>
      </CollapsibleContent>
    </Collapsible>
  );
}

/** What one rendered answer needs — the live result and a stored history turn both map onto it. */
interface AnswerCardProps {
  answer: string | null;
  question: string;
  sql: string | null;
  columns: string[] | null;
  rows: Record<string, unknown>[] | null;
  rowCount: number | null;
  truncated: boolean;
  charts: ChartSpec[];
  capped?: boolean;
  /** Footer summary text (model · effort · …). */
  footer: string;
  feedback: ReactNode;
  /** Create data source / Create report / Add to Insight Report row. */
  actions?: ReactNode;
}

function AnswerCard({
  answer,
  question,
  sql,
  columns,
  rows,
  rowCount,
  truncated,
  charts,
  capped = false,
  footer,
  feedback,
  actions,
}: AnswerCardProps) {
  return (
    <div className="space-y-2">
      <Card>
        <CardContent className="space-y-3 pt-4">
          <PortalMarkdown
            content={answer}
            emptyState={<p className="text-sm text-muted-foreground">No answer was recorded.</p>}
          />
          {capped && (
            <p className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              Stopped after the tool-call limit; answer may be partial.
            </p>
          )}
          <QuickQuestionCharts
            charts={charts}
            columns={columns}
            rows={rows}
            truncated={truncated}
            question={question}
          />
          {sql && <SqlBlock sql={sql} />}
          {columns && rows && rows.length > 0 && (
            <div className="space-y-1">
              {truncated && (
                <p className="text-xs text-muted-foreground">
                  Showing the first {rows.length.toLocaleString()} rows of {rowCount?.toLocaleString() ?? "many"}.
                </p>
              )}
              <ResultsTable columns={columns} rows={rows} question={question} />
            </div>
          )}
          {actions}
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
            <span>{footer}</span>
            {feedback}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/** What one answer's action buttons act on. */
interface AnswerActionTarget {
  /** Which rendered answer this target belongs to (NOT the promoted row: a chart-only turn and its source share that). */
  turnKey: string;
  /** The executed row to promote: for a chart-only turn, its source row. */
  logId: string;
  sql: string;
  /**
   * The answer and question of the turn that PRODUCED the executed result.
   * Empty when that turn is not available (never the chart-only turn's own text).
   */
  answer: string;
  question: string;
  dataSourceId: string;
}

interface AnswerActionsProps {
  target: AnswerActionTarget | null;
  canManageReporting: boolean;
  canAddToInsight: boolean;
  busy: boolean;
  /** The promote refusal to show, when it belongs to THIS answer. */
  error: KbPromoteError | null;
  onCreateDatasource: (target: AnswerActionTarget) => void;
  onCreateReport: (target: AnswerActionTarget) => void;
  onAddToInsight: (target: AnswerActionTarget) => void;
  onAskWithColumns: (target: AnswerActionTarget, columns: string[]) => void;
}

/**
 * Hidden for an answer that could never be promoted (no SQL, or no query-log
 * id: nothing durable to build from) and for a viewer without the permission.
 * Every other refusal is a reason the user should HEAR, so it surfaces inline.
 */
function AnswerActions({
  target,
  canManageReporting,
  canAddToInsight,
  busy,
  error,
  onCreateDatasource,
  onCreateReport,
  onAddToInsight,
  onAskWithColumns,
}: AnswerActionsProps) {
  if (!target || !target.sql || !target.logId) return null;
  if (!canManageReporting && !canAddToInsight) return null;
  return (
    <div className="space-y-2" data-testid="qq-actions">
      <div className="flex flex-wrap items-center gap-2">
        {canManageReporting && (
          <>
            <Button variant="outline" size="sm" disabled={busy} onClick={() => onCreateDatasource(target)}>
              <TableProperties className="mr-2 h-4 w-4" />
              Create data source
            </Button>
            <Button variant="outline" size="sm" disabled={busy} onClick={() => onCreateReport(target)}>
              <FileBarChart className="mr-2 h-4 w-4" />
              Create report
            </Button>
          </>
        )}
        {canAddToInsight && (
          <Button variant="outline" size="sm" onClick={() => onAddToInsight(target)}>
            <Sparkles className="mr-2 h-4 w-4" />
            Add to Insight Report
          </Button>
        )}
      </div>
      {error && (
        <div role="alert" className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          <p>{error.message}</p>
          {error.code === "select_star_not_allowed" && error.columns && error.columns.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={() => onAskWithColumns(target, error.columns!)}
            >
              Ask again listing these columns
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

/** `model · effort · N tool calls · Ns`; effort is omitted for rows that predate it (null). */
function liveFooter(result: QuickQuestionResult): string {
  const parts = [result.model];
  if (result.effort) parts.push(EFFORT_LABELS[result.effort]);
  parts.push(`${result.tools_used.length} tool call${result.tools_used.length === 1 ? "" : "s"}`);
  parts.push(`${(result.elapsed_ms / 1000).toFixed(1)}s`);
  return parts.join(" · ");
}

function historyFooter(detail: QuickQuestionHistoryDetail): string {
  const parts: string[] = [];
  if (detail.model) parts.push(detail.model);
  if (detail.effort) parts.push(EFFORT_LABELS[detail.effort]);
  return parts.join(" · ");
}

function AssistantTurn({
  turn,
  question,
  actions,
}: {
  turn: QuickQuestionTurn;
  question: string;
  actions?: ReactNode;
}) {
  const result: QuickQuestionResult | undefined = turn.result;
  if (turn.error) {
    return (
      <div
        role="alert"
        className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm"
      >
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden="true" />
        <span>{turn.error}</span>
      </div>
    );
  }
  return (
    <AnswerCard
      answer={turn.content}
      question={question}
      sql={result?.sql ?? null}
      columns={result?.columns ?? null}
      rows={result?.rows ?? null}
      rowCount={result?.row_count ?? null}
      truncated={result?.truncated ?? false}
      charts={result?.charts ?? []}
      capped={result?.capped}
      footer={result ? liveFooter(result) : ""}
      feedback={result ? <QueryFeedback queryType="sql_rag" queryId={result.log_id} compact /> : null}
      actions={actions}
    />
  );
}

function QuickQuestionPage() {
  const { token } = useAuth();
  usePortalData();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { can, isOwner, isAdmin, isPlatformAdmin } = usePermissions();
  // Create data source / Create report both create a Reporting datasource, and
  // org_member never creates a data source (Steve 2026-10-15): admin and above
  // only. CP grants `manage` on reporting to org_owner / org_admin /
  // platform_admin and not to org_member, who holds `create`.
  const canManageReporting = can("manage", "reporting");
  const canAddToInsight = canAuthorInsightReports({ isOwner, isAdmin, isPlatformAdmin });

  const [dataSources, setDataSources] = useState<DataSource[]>([]);
  const [loadingDataSources, setLoadingDataSources] = useState(true);
  const [selectedDataSourceId, setSelectedDataSourceId] = useState<string>(() => readLastSource());
  const [question, setQuestion] = useState("");
  const [history, setHistory] = useState<QuickQuestionHistoryItem[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [selectedHistoryId, setSelectedHistoryId] = useState<string | undefined>();
  // Keyed by the history id it was loaded for, so a stale item is never shown
  // for the newly selected one (the effect below only runs after paint).
  const [historyLoad, setHistoryLoad] = useState<{
    id: string;
    detail: QuickQuestionHistoryDetail | null;
    /** "notfound": older data plane without the route (harmless); "failed": a real error. */
    error: "notfound" | "failed" | null;
  } | null>(null);
  const [historyRetry, setHistoryRetry] = useState(0);
  // The answer the promote dialog / Insight dialog is acting on. One promote
  // flow at a time, so a single target (and a single error banner) is enough.
  const [promoteTarget, setPromoteTarget] = useState<AnswerActionTarget | null>(null);
  const [promoteMode, setPromoteMode] = useState<"datasource" | "report">("datasource");
  const [insightTarget, setInsightTarget] = useState<AnswerActionTarget | null>(null);
  const [effort, setEffort] = useState<QuickQuestionEffort>(() => readEffort());
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  const loadHistory = useCallback(async () => {
    if (!token) return;
    setLoadingHistory(true);
    try {
      setHistory(await quickQuestionApi.history(token, 50));
    } catch (error) {
      console.error("Failed to load Quick Question history:", error);
    } finally {
      setLoadingHistory(false);
    }
  }, [token]);

  const {
    analysis: promoteAnalysis,
    commitError: promoteCommitError,
    promoting,
    error: promoteError,
    start: startPromotion,
    confirm: confirmPromotion,
    cancel: cancelPromotion,
    clearError: clearPromoteError,
  } = useKbPromotion(token);

  const { turns, isRunning, phase, ask, reset, conversationId } = useQuickQuestionJob({
    token,
    dataSourceId: selectedDataSourceId,
    effort,
    onCompleted: () => {
      void loadHistory();
    },
  });

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      setLoadingDataSources(true);
      try {
        const response = await sqlRagApi.listDataSources(token, false);
        if (cancelled) return;
        const validated = response.filter((ds: DataSource) => ds.is_validated);
        setDataSources(validated);
        // Default = last used when it is still offered, else the only/first one.
        setSelectedDataSourceId((current) => {
          if (current && validated.some((ds) => ds.id === current)) return current;
          return validated.length === 1 ? validated[0].id : "";
        });
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to load data sources:", error);
          toast.error("Failed to load data sources");
        }
      } finally {
        if (!cancelled) setLoadingDataSources(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  // A selected history item loads its stored turn (charts, rows, model) so it
  // renders exactly like a live answer. A 404 (a data plane without the detail
  // route) quietly falls back to the answer-only list item, which is all the
  // light list endpoint carries; any other failure says so and offers a retry.
  useEffect(() => {
    if (!token || !selectedHistoryId) return;
    let cancelled = false;
    const id = selectedHistoryId;
    quickQuestionApi
      .historyDetail(token, id)
      .then((detail) => {
        if (!cancelled) setHistoryLoad({ id, detail, error: null });
      })
      .catch((error) => {
        if (cancelled) return;
        console.error("Failed to load Quick Question history item:", error);
        const notFound = error instanceof PortalApiError && error.status === 404;
        setHistoryLoad({ id, detail: null, error: notFound ? "notfound" : "failed" });
      });
    return () => {
      cancelled = true;
    };
  }, [token, selectedHistoryId, historyRetry]);

  const loadedHistory = historyLoad && historyLoad.id === selectedHistoryId ? historyLoad : null;
  // `loadedHistory` already guards the stale-frame race (it's keyed by the id
  // it was fetched for); this second check is a belt-and-braces guard against
  // the RESPONSE BODY itself disagreeing with the id it was fetched by (a
  // backend/proxy contract bug), not against staleness — kept deliberately.
  const historyDetail =
    loadedHistory?.detail && loadedHistory.detail.log_id === selectedHistoryId ? loadedHistory.detail : null;
  const historyDetailLoading = Boolean(selectedHistoryId) && !loadedHistory;
  const historyDetailFailed = loadedHistory?.error === "failed";

  const retryHistoryDetail = () => {
    setHistoryLoad(null);
    setHistoryRetry((n) => n + 1);
  };

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, [turns.length, phase]);

  const hasActiveConversation = turns.length > 0;

  const handleSourceChange = (id: string) => {
    setSelectedDataSourceId(id);
    rememberSource(id);
  };

  const handleCreate = (mode: "datasource" | "report", target: AnswerActionTarget) => {
    setPromoteTarget(target);
    setPromoteMode(mode);
    void startPromotion(target.logId);
  };

  const handlePromoteConfirm = (args: KbPromoteConfirmArgs) => {
    const target = promoteTarget;
    if (!target) return Promise.resolve();
    const mode = promoteMode;
    return confirmPromotion(args, (created) => {
      if (mode === "report") {
        // The custom-report wizard consumes this handoff and IS the
        // report-creation step; nothing more is needed server-side.
        const opened = openReportWizardWithHandoff(created, target.answer || null, target.logId);
        toast.success(
          `Created datasource "${created.name}"`,
          opened ? undefined : { description: "The report builder could not be opened automatically." },
        );
      } else {
        toast.success(`Created datasource "${created.name}"`, {
          action: {
            label: "View data source",
            onClick: () =>
              void navigate({
                to: "/portal/apps/reporting/datasources/$slug",
                params: { slug: created.slug },
              }),
          },
        });
      }
    });
  };

  // D27's recovery for SELECT *: pre-fill the composer rather than submitting
  // (the submit handler reads the question from state).
  const handleAskWithColumns = (target: AnswerActionTarget, columns: string[]) => {
    const listing = `List these columns explicitly: ${columns.join(", ")}`;
    setQuestion(target.question ? `${target.question}. ${listing}` : listing);
    clearPromoteError();
    setSelectedHistoryId(undefined);
  };

  const renderActions = (target: AnswerActionTarget | null) => (
    <AnswerActions
      target={target}
      canManageReporting={canManageReporting}
      canAddToInsight={canAddToInsight}
      busy={promoting}
      error={target && promoteTarget?.turnKey === target.turnKey ? promoteError : null}
      onCreateDatasource={(t) => handleCreate("datasource", t)}
      onCreateReport={(t) => handleCreate("report", t)}
      onAddToInsight={setInsightTarget}
      onAskWithColumns={handleAskWithColumns}
    />
  );

  const handleEffortChange = (value: string) => {
    if (!isEffort(value)) return;
    setEffort(value);
    rememberEffort(value);
  };

  const handleSend = async () => {
    if (!question.trim() || !selectedDataSourceId || isRunning) return;
    const q = question;
    setQuestion("");
    setSelectedHistoryId(undefined);
    await ask(q);
  };

  const handleNew = () => {
    reset();
    setQuestion("");
    setSelectedHistoryId(undefined);
  };

  const historyItems: QueryHistoryItem[] = useMemo(
    () =>
      history.map((h) => ({
        id: h.log_id,
        title: h.question,
        timestamp: h.created_at ? parseUtcDate(h.created_at) : new Date(),
        status: h.answer ? "success" : "error",
        feedback: h.user_feedback === "positive" || h.user_feedback === "negative" ? h.user_feedback : undefined,
        metadata: h.row_count != null ? { label: `${h.row_count} rows` } : undefined,
      })),
    [history],
  );

  const selectedHistory = history.find((h) => h.log_id === selectedHistoryId);

  const historySidebar = (
    <QueryHistorySidebar
      title="Quick Questions"
      icon={History}
      items={historyItems}
      currentId={selectedHistoryId}
      onSelect={(item) => {
        setSelectedHistoryId(item.id);
        setMobileSidebarOpen(false);
      }}
      onNew={() => {
        handleNew();
        setMobileSidebarOpen(false);
      }}
      isLoading={loadingHistory}
      searchPlaceholder="Search questions..."
      newButtonLabel="+ New question"
    />
  );

  /** The user turn a given assistant turn answered. */
  const questionBefore = (index: number): string => {
    for (let i = index - 1; i >= 0; i--) {
      if (turns[i].role === "user") return turns[i].content;
    }
    return "";
  };

  /**
   * A live answer's action target. A chart-only turn acts on its SOURCE row, so
   * the question and answer come from the turn that produced that row; the
   * chart-only turn's own text ("give me two more charts") describes neither
   * the data nor the report. When the source turn is not in the transcript the
   * text is left empty (neutral fallback) rather than borrowing the chart-only one.
   */
  const liveTarget = (turn: QuickQuestionTurn, index: number): AnswerActionTarget | null => {
    const r = turn.result;
    if (!r?.sql) return null;
    const sourceId = r.chart_log_id && r.chart_log_id !== r.log_id ? r.chart_log_id : null;
    let answer = turn.content;
    let question = questionBefore(index);
    if (sourceId) {
      const sourceIndex = turns.findIndex((t) => t.role === "assistant" && t.result?.log_id === sourceId);
      answer = sourceIndex >= 0 ? turns[sourceIndex].content : "";
      question = sourceIndex >= 0 ? questionBefore(sourceIndex) : "";
    }
    return {
      turnKey: turn.id,
      logId: sourceId ?? r.log_id,
      sql: r.sql,
      answer,
      question,
      dataSourceId: selectedDataSourceId,
    };
  };

  // The history detail carries no question/answer for a chart-only turn's
  // source, so look the source up in the loaded list; without it, stay neutral.
  const historyTarget: AnswerActionTarget | null = (() => {
    if (!historyDetail?.sql || !historyDetail.data_source_id) return null;
    const sourceId =
      historyDetail.source_log_id && historyDetail.source_log_id !== historyDetail.log_id
        ? historyDetail.source_log_id
        : null;
    const own = sourceId ? undefined : { question: historyDetail.question, answer: historyDetail.answer ?? "" };
    const source = sourceId ? history.find((h) => h.log_id === sourceId) : undefined;
    return {
      turnKey: `history:${historyDetail.log_id}`,
      logId: sourceId ?? historyDetail.log_id,
      sql: historyDetail.sql,
      answer: own?.answer ?? source?.answer ?? "",
      question: own?.question ?? source?.question ?? "",
      dataSourceId: historyDetail.data_source_id,
    };
  })();

  return (
    <>
      <Helmet>
        <title>Quick Question | Knowledgebase Database</title>
      </Helmet>
      <div className="flex h-full">
        {isMobile ? (
          <Sheet open={mobileSidebarOpen} onOpenChange={setMobileSidebarOpen}>
            <SheetContent side="left" className="w-80 p-0">
              <SheetHeader className="sr-only">
                <SheetTitle>Quick Question history</SheetTitle>
                <SheetDescription>Your previous quick questions</SheetDescription>
              </SheetHeader>
              {historySidebar}
            </SheetContent>
          </Sheet>
        ) : (
          <div className="hidden w-80 shrink-0 border-r md:block">{historySidebar}</div>
        )}

        <div className="flex min-w-0 flex-1 flex-col">
          <div className="space-y-4 p-4 md:p-6">
            <div className="flex items-start gap-3">
              {isMobile && (
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9 flex-shrink-0"
                  aria-label="Open history"
                  onClick={() => setMobileSidebarOpen(true)}
                >
                  <History className="h-4 w-4" />
                </Button>
              )}
              <div className="min-w-0 flex-1">
                <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900 md:text-4xl">
                  <Zap className="h-6 w-6 text-cyan-600" aria-hidden="true" />
                  Quick Question
                </h1>
                <p className="mt-1 text-sm text-slate-600 md:mt-2 md:text-lg">
                  An AI analyst works through your data step by step, checking its own SQL, and answers in
                  plain English. For the guided flow that asks you to clarify first, use Ask Database.
                </p>
              </div>
            </div>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Database className="h-4 w-4" />
                  Data source
                </CardTitle>
                <CardDescription>
                  Pick the database to ask about. It stays fixed for the conversation.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="qq-data-source">Data Source</Label>
                  {hasActiveConversation && (
                    <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={handleNew}>
                      <Sparkles className="mr-1 h-3 w-3" />
                      New conversation
                    </Button>
                  )}
                </div>
                <Select
                  value={selectedDataSourceId}
                  onValueChange={handleSourceChange}
                  disabled={loadingDataSources || hasActiveConversation}
                >
                  <SelectTrigger id="qq-data-source" aria-label="Data Source">
                    <SelectValue placeholder="Select a database..." />
                  </SelectTrigger>
                  <SelectContent>
                    {dataSources.map((ds) => (
                      <SelectItem key={ds.id} value={ds.id}>
                        <div className="flex items-center gap-2">
                          <Database className="h-4 w-4" />
                          <span>{ds.retired_at_utc ? `${ds.name} (retired)` : ds.name}</span>
                          {ds.database_name && (
                            <span className="text-xs text-muted-foreground">({ds.database_name})</span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {dataSources.length === 0 && !loadingDataSources && (
                  <p className="text-sm text-muted-foreground">
                    No databases configured. Contact your administrator to set up a data source.
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="flex-1 space-y-4 overflow-y-auto px-4 pb-4 md:px-6" data-testid="qq-transcript">
            {selectedHistory && !hasActiveConversation && (
              <div className="space-y-3">
                <div className="ml-auto max-w-[85%] rounded-lg bg-slate-100 p-3 text-sm dark:bg-slate-800">
                  {selectedHistory.question}
                </div>
                {historyDetailLoading ? (
                  <div
                    role="status"
                    className="flex items-center gap-2 rounded-lg border border-dashed p-3 text-sm text-muted-foreground"
                  >
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    <span>Loading answer…</span>
                  </div>
                ) : (
                  <>
                  {historyDetailFailed && (
                    <div
                      role="alert"
                      className="flex flex-wrap items-center gap-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-sm text-amber-900"
                    >
                      <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
                      <span>Couldn&apos;t load the full answer.</span>
                      <Button variant="outline" size="sm" className="h-7" onClick={retryHistoryDetail}>
                        Try again
                      </Button>
                    </div>
                  )}
                  <AnswerCard
                    answer={historyDetail?.answer ?? selectedHistory.answer}
                    question={selectedHistory.question}
                    sql={historyDetail?.sql ?? selectedHistory.sql}
                    columns={historyDetail?.columns ?? null}
                    rows={historyDetail?.rows ?? null}
                    rowCount={historyDetail?.row_count ?? selectedHistory.row_count}
                    truncated={historyDetail?.truncated ?? false}
                    charts={historyDetail?.charts ?? []}
                    footer={historyDetail ? historyFooter(historyDetail) : ""}
                    actions={renderActions(historyTarget)}
                    feedback={
                      <QueryFeedback
                        queryType="sql_rag"
                        queryId={selectedHistory.log_id}
                        initialFeedback={
                          selectedHistory.user_feedback === "positive" || selectedHistory.user_feedback === "negative"
                            ? selectedHistory.user_feedback
                            : null
                        }
                        compact
                      />
                    }
                  />
                  </>
                )}
              </div>
            )}
            {turns.map((turn, index) =>
              turn.role === "user" ? (
                <div
                  key={turn.id}
                  className="ml-auto max-w-[85%] whitespace-pre-wrap rounded-lg bg-slate-100 p-3 text-sm dark:bg-slate-800"
                >
                  {turn.content}
                </div>
              ) : (
                <AssistantTurn
                  key={turn.id}
                  turn={turn}
                  question={questionBefore(index)}
                  actions={renderActions(liveTarget(turn, index))}
                />
              ),
            )}
            {isRunning && (
              <div
                role="status"
                aria-live="polite"
                className="flex items-center gap-2 rounded-lg border border-dashed p-3 text-sm text-muted-foreground"
              >
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                <span>{phaseLabel(phase)}</span>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          <div className="border-t bg-background p-4 md:p-6">
            <Label htmlFor="qq-question" className="sr-only">
              Your question
            </Label>
            <div className="flex items-end gap-2">
              <Textarea
                id="qq-question"
                placeholder={
                  hasActiveConversation
                    ? "Ask a follow-up… (Enter to send, Shift+Enter for a new line)"
                    : "e.g. Which five customers owe us the most right now? (Enter to send)"
                }
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    void handleSend();
                  }
                }}
                rows={2}
                disabled={isRunning}
              />
              <div className="w-32 shrink-0 space-y-1">
                <Label htmlFor="qq-effort" className="text-xs">
                  Effort
                </Label>
                <Select value={effort} onValueChange={handleEffortChange} disabled={isRunning} name="effort">
                  <SelectTrigger id="qq-effort" aria-label="Effort">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {QUICK_QUESTION_EFFORTS.map((e) => (
                      <SelectItem key={e} value={e}>
                        {EFFORT_LABELS[e]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={() => void handleSend()}
                disabled={isRunning || !question.trim() || !selectedDataSourceId}
                aria-label="Send question"
              >
                {isRunning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
            <p className="mt-1 text-[11px] text-muted-foreground">
              Higher effort thinks longer and answers more carefully; it takes longer and costs more.
            </p>
            {conversationId && (
              <p className="mt-1 text-[11px] text-muted-foreground">
                Follow-ups keep the last 20 turns as context.
              </p>
            )}
          </div>
        </div>
      </div>

      <PromoteToReportingDialog
        open={promoteAnalysis !== null}
        analysis={promoteAnalysis}
        submitting={promoting}
        commitError={promoteCommitError}
        confirmLabel={
          promoteMode === "report" ? "Create data source and open report builder" : "Create data source"
        }
        onCancel={cancelPromotion}
        onConfirm={handlePromoteConfirm}
      />
      <AddToInsightReportDialog
        open={insightTarget !== null}
        token={token}
        dataSourceId={insightTarget?.dataSourceId ?? ""}
        queryLogId={insightTarget?.logId ?? ""}
        question={insightTarget?.question ?? ""}
        onClose={() => setInsightTarget(null)}
      />
    </>
  );
}
